import React, { Component } from 'react';
import { Paper, Tab, Tabs, Popover } from '@mui/material';
import { Button, Tooltip } from '@trucktrax/trucktrax-common';
import { PermissionAccess, ProductLine, TicketDto, VoidFunc } from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import { connect } from 'react-redux';
import ReactToPrint from 'react-to-print';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import styles from './Ticket.module.css';
import TicketDetails from './TicketDetails';
import RouteInfo from './RouteInfo';
import UpdateDeliveryLocationModal from './UpdateDeliveryLocationModal';
import TicketQualityFactors from './TicketQualityFactors';
import CardImage from '../../forms/CardImage';
import {
  userHasEditPermission,
  userHasEditPermissionInAnyRegion,
} from '../../../../services/permissionsService';
import printerBlue from '../../../../assets/img/printer-blue.svg';
import fetchReceiptPDF from '../../../../services/receiptService';
import { acknowledgeIncomingTicket, closeOpenTicket } from '../../../../services/ticketsService';
import { effectiveProductLine } from '../../../../util/ticketUtil';
import { canAcknowledgeMaterialReceipt } from '../../../../util/statusesUtil';
import { hasFeatureFlagEditAccess } from '../../../../util/featureFlagUtil';
import { openMaterialReceivedConfirm } from '../../../geotrax/incomingMaterials/materialReceivedConfirm';
import { ConnectedFunction, MaterialReceiptFields } from '../../../../types';
import {
  centerTicketPrintView,
  updateTicketView,
  clearSelectedTicket,
  needToRefreshData,
} from '../../../../store/actions/ticketActions';
import { openModal, closeModal } from '../../../../store/actions/errorModalActions';
import { openSnackbar } from '../../../../store/actions/snackbarActions';
import {
  VIEW_TICKET_DETAILS,
  VIEW_TICKET_QUALITY_FACTORS,
  VIEW_TICKET_ROUTE,
  DOWNLOAD_RECEIPTS,
  READYMIX_VALUE,
  CEMENT_VALUE,
  AGGREGATE_VALUE,
  ADMIN_LABELS,
  TICKET_SINGLE_COSE,
  ACCENT,
  ARE_YOU_SURE,
  CONFIRM_CLOSE_SINGLE_TICKET,
  CANCEL_LABEL,
  TICKET_CLOSED_SUCCESS,
  TICKET_CLOSED_ERROR,
  SUCCESS,
  ERROR,
  INCOMING_MATERIALS_FLAG,
  PERMISSION_NAMES,
} from '../../../../constants/appConstants';
import { OPEN_TICKETS_TEXT } from '../../../../constants/navConstants';

// Constants
const PRINT_TYPE = {
  SECTION: 'section',
  ENTIRE_TICKET: 'entire ticket',
} as const;

const PRINT_TIMEOUTS = {
  SECTION: 650,
  ENTIRE_TICKET_TAB_SWITCH: 3000,
  ENTIRE_TICKET_MAP_LOAD: 1500,
} as const;

// Types
type PrintType = (typeof PRINT_TYPE)[keyof typeof PRINT_TYPE];

export interface TicketProps {
  selectedTicket?: TicketDto;
  ticketView?: string;
  updateTicketView: (selected: string) => void;
  centerTicketPrintView: (isPrintView: boolean) => void;
  selectedProductLine?: ProductLine;
  /**
   * DEV-3924: per-ticket product line from the ticket's origination plant (see
   * RouteInfoProps.productLineOverride for the undefined/null/value semantics).
   * Threaded down to RouteInfo and used for product-line-keyed UI such as the
   * Quality Factors tab.
   */
  productLineOverride?: ProductLine | null;
  /** DEV-3924: workflow fallback for unresolvable driver externality (IM passes true). */
  unresolvedDriverIsExternal?: boolean;
  userPermission: any;
  // Divert gate inputs: the user's assigned product lines from the JWT (set at login).
  // secondaryProductLines rehydrates from localStorage as a comma-joined string after a
  // reload, so consumers must stick to .includes() (see canEditDeliveryLocation).
  primaryProductLine?: ProductLine;
  secondaryProductLines?: ProductLine[];
  shrinkPopover?: () => void;
  history: { push: (path: string) => void };
  isOpenTicket: boolean;
  openModal: typeof openModal;
  closeModal: typeof closeModal;
  clearSelectedTicket: VoidFunc;
  closePopover: () => void;
  openSnackbar: ConnectedFunction<typeof openSnackbar>;
  needToRefreshData: (needToRefreshData: boolean) => void;
  ticketsPermissionAccess?: string;
  // DEV-4153: mark-as-received gate inputs — Edit access is checked against
  // the IncomingMaterials flag for the current region.
  currentRegion?: { url?: string };
  acknowledgeIncomingTicket: (ticketId: number) => Promise<TicketDto & MaterialReceiptFields>;
  /** DEV-4153: IM drill-down marker (IncomingTicketsPopover passes true); threaded to TicketDetails. */
  isIncomingMaterials?: boolean;
}

interface TicketState {
  printButtonRef?: HTMLButtonElement;
  isPrintOpen: boolean;
  printType: PrintType;
  enableDownloadReceipts: boolean;
  showDeliveryLocationModal: boolean;
}

const wait = (ms: number): Promise<void> =>
  new Promise(resolve => {
    global.setTimeout(resolve, ms);
  });

export class Ticket extends Component<TicketProps, TicketState> {
  static defaultProps: Partial<TicketProps> = {
    selectedTicket: undefined,
    ticketView: VIEW_TICKET_DETAILS,
    selectedProductLine: undefined,
    productLineOverride: undefined,
    unresolvedDriverIsExternal: false,
    isOpenTicket: false,
    isIncomingMaterials: false,
  };

  private eventTimeout?: NodeJS.Timeout;

  private printContentRef = React.createRef<HTMLDivElement>();

  constructor(props: TicketProps) {
    super(props);
    this.state = {
      isPrintOpen: false,
      printType: PRINT_TYPE.SECTION,
      enableDownloadReceipts: false,
      showDeliveryLocationModal: false,
    };
  }

  componentDidMount(): void {
    this.triggerTabResizeWorkaround();
    this.updatePermissions();
  }

  componentDidUpdate(): void {
    this.updatePermissions();
  }

  componentWillUnmount(): void {
    if (this.eventTimeout) {
      clearTimeout(this.eventTimeout);
    }
  }

  // Lifecycle helpers
  private triggerTabResizeWorkaround(): void {
    this.eventTimeout = global.setTimeout(() => window.dispatchEvent(new CustomEvent('resize')), 0);
  }

  private updatePermissions = (): void => {
    const enableDownloadReceipts = userHasEditPermissionInAnyRegion(
      this.props.userPermission,
      DOWNLOAD_RECEIPTS,
    );

    if (enableDownloadReceipts !== this.state.enableDownloadReceipts) {
      this.setState({ enableDownloadReceipts });
    }
  };

  // Tab management
  private handleTabChange = (event?: React.ChangeEvent<{}>, selected?: string): void => {
    if (selected && this.props.ticketView !== selected) {
      this.props.updateTicketView(selected);
    }
  };

  private shouldDisplayQualityFactorsTab(): boolean {
    const { selectedTicket, productLineOverride, selectedProductLine } = this.props;
    const productLine = effectiveProductLine(productLineOverride, selectedProductLine);
    return !!(selectedTicket?.closed && productLine === READYMIX_VALUE);
  }

  private getValidatedView(): string {
    const { ticketView } = this.props;
    const shouldDisplayQFTab = this.shouldDisplayQualityFactorsTab();

    if (ticketView === VIEW_TICKET_QUALITY_FACTORS && !shouldDisplayQFTab) {
      this.props.updateTicketView(VIEW_TICKET_DETAILS);
      return VIEW_TICKET_DETAILS;
    }

    return ticketView || VIEW_TICKET_DETAILS;
  }

  // Print functionality
  private onPrintOpen = (event?: React.MouseEvent<HTMLButtonElement>): void => {
    this.setState({
      printButtonRef: event?.currentTarget,
      isPrintOpen: true,
    });
  };

  private onPrintClose = (): void => {
    this.setState({
      printButtonRef: undefined,
      isPrintOpen: false,
    });
    this.props.centerTicketPrintView(false);
  };

  private preparePrintContent = async (printType: PrintType): Promise<void> => {
    this.setState({ printType });
    this.props.centerTicketPrintView(true);

    if (printType === PRINT_TYPE.ENTIRE_TICKET) {
      await this.ensureRouteTabLoaded();
    } else {
      await wait(PRINT_TIMEOUTS.SECTION);
    }
  };

  private ensureRouteTabLoaded = async (): Promise<void> => {
    const { ticketView } = this.props;

    if (ticketView !== VIEW_TICKET_ROUTE) {
      this.props.updateTicketView(VIEW_TICKET_ROUTE);
      await wait(PRINT_TIMEOUTS.ENTIRE_TICKET_TAB_SWITCH);
    } else {
      await wait(PRINT_TIMEOUTS.ENTIRE_TICKET_MAP_LOAD);
    }
  };

  // Ticket management
  private confirmCloseTicket = (): void => {
    const modalBody = (
      <span>
        <p>{TICKET_SINGLE_COSE}</p>
      </span>
    );

    this.props.openModal({
      modalType: ACCENT,
      modalTitle: ARE_YOU_SURE,
      modalBody,
      modalOpen: true,
      acceptDialog: this.closeTicket,
      acceptText: CONFIRM_CLOSE_SINGLE_TICKET,
      cancelText: CANCEL_LABEL,
      disabled: false,
      noActions: false,
    });
  };

  private closeTicket = async (): Promise<void> => {
    if (!this.props.selectedTicket) return;

    const ticket = { ...this.props.selectedTicket, closed: true };
    const update = await closeOpenTicket(ticket);

    if (update instanceof Error) {
      this.showToast(TICKET_CLOSED_ERROR, ERROR, update.message);
    } else {
      this.showToast(TICKET_CLOSED_SUCCESS, SUCCESS);
      this.props.clearSelectedTicket();
      this.props.needToRefreshData(true);
      this.props.closePopover();
    }

    this.props.closeModal();
  };

  private showToast = (message: string, type: string, error?: string): void => {
    this.props.openSnackbar({
      snackbarBody: error ? `${message} ${error}` : message,
      dataTest: `close-tickets-${type.toLowerCase()}-snackbar`,
      snackbarType: type as any,
    });
  };

  // Receipt functionality
  private downloadReceipt = (): void => {
    const receiptUrl = this.props.selectedTicket?.receiptUrl;
    if (receiptUrl) {
      fetchReceiptPDF(receiptUrl);
    }
  };

  private hasReceipt(): boolean {
    return !!this.props.selectedTicket?.receiptUrl;
  }

  // Render helpers
  private renderBodyElement = (
    element: JSX.Element,
    isVisible: boolean,
    breakPage?: boolean,
  ): JSX.Element | null => {
    if (isVisible) {
      return element;
    }

    if (this.state.isPrintOpen && this.state.printType === PRINT_TYPE.ENTIRE_TICKET) {
      const className = breakPage
        ? cx(styles.printVisibleOnly, styles.pageBreak)
        : styles.printVisibleOnly;

      return <span className={className}>{element}</span>;
    }

    return null;
  };

  private renderTabContent(): JSX.Element {
    const { ticketView, selectedTicket, shrinkPopover, history } = this.props;

    const ticketDetails = (
      <TicketDetails
        selectedTicket={selectedTicket}
        plant={selectedTicket?.plant}
        plantList={[]}
        shrinkPopover={shrinkPopover}
        history={history}
        onEditDeliveryLocation={
          this.canEditDeliveryLocation() ? this.openDeliveryLocationModal : undefined
        }
        onMarkAsReceived={this.canMarkAsReceived() ? this.openMarkAsReceivedConfirm : undefined}
        isIncomingMaterials={this.props.isIncomingMaterials}
      />
    );

    const routeInfo = (
      <RouteInfo
        productLineOverride={this.props.productLineOverride}
        unresolvedDriverIsExternal={this.props.unresolvedDriverIsExternal}
      />
    );
    const qualityFactors = <TicketQualityFactors ticket={selectedTicket} />;

    return (
      <>
        <h1 className={styles.printHeader}>Ticket #{selectedTicket?.ticketNumber}</h1>
        {this.renderBodyElement(ticketDetails, ticketView === VIEW_TICKET_DETAILS)}
        {this.renderBodyElement(routeInfo, ticketView === VIEW_TICKET_ROUTE)}
        {this.renderBodyElement(qualityFactors, ticketView === VIEW_TICKET_QUALITY_FACTORS, true)}
      </>
    );
  }

  private renderPrintButton(): JSX.Element {
    const buttonStyle = this.state.isPrintOpen ? styles.printBtnClicked : styles.printBtn;

    const renderPrintEntireTicketButton = () => (
      <button className={styles.printTrigger}>Entire ticket</button>
    );

    const renderPrintTicketSectionButton = () => (
      <button className={styles.printTrigger}>Only this section</button>
    );

    return (
      <span>
        <Popover
          open={this.state.isPrintOpen}
          onClose={this.onPrintClose}
          anchorEl={this.state.printButtonRef}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <div className={styles.printPopover}>
            <ReactToPrint
              trigger={renderPrintEntireTicketButton}
              content={() => this.printContentRef.current}
              onBeforeGetContent={() => this.preparePrintContent(PRINT_TYPE.ENTIRE_TICKET)}
              onAfterPrint={this.onPrintClose}
            />
            <ReactToPrint
              trigger={renderPrintTicketSectionButton}
              content={() => this.printContentRef.current}
              onBeforeGetContent={() => this.preparePrintContent(PRINT_TYPE.SECTION)}
              onAfterPrint={this.onPrintClose}
            />
          </div>
        </Popover>
        <button className={buttonStyle} onClick={this.onPrintOpen}>
          <CardImage src={printerBlue} className={styles.printIcon} />
          <span>Print</span>
          <i className="icon-keyboard-arrow-down" />
        </button>
      </span>
    );
  }

  private renderCloseTicketButton(): JSX.Element | null {
    const hasPermission = this.props.ticketsPermissionAccess === PermissionAccess.Edit;

    if (!hasPermission) return null;

    return (
      <div className={styles.enabledCloseSection}>
        <div key="action-buttons">
          <button onClick={this.confirmCloseTicket}>
            <RemoveCircleOutline />
            <span>{ADMIN_LABELS.CLOSE_TICKET_SINGULAR}</span>
          </button>
        </div>
      </div>
    );
  }

  // Shared gate for the inline Edit control rendered next to the delivery address in
  // TicketDetails. Diverting a ticket requires BOTH the dedicated TicketDiversions Edit
  // permission in the ticket's own (source) region AND the user being assigned (primary
  // or secondary) to the ticket's product line. This mirrors the rule enforced
  // server-side in TicketsService.
  private canEditDeliveryLocation(): boolean {
    const {
      primaryProductLine,
      productLineOverride,
      secondaryProductLines,
      selectedProductLine,
      selectedTicket,
      userPermission,
    } = this.props;
    // Guard a missing region URL explicitly rather than letting the lookup miss on an
    // 'undefined' key: a ticket without a region cannot be diverted.
    const ticketRegionUrl = selectedTicket?.region?.url;
    const canDivert =
      !!ticketRegionUrl &&
      userHasEditPermission(userPermission, ticketRegionUrl, PERMISSION_NAMES.TICKET_DIVERSIONS);
    // Gate on the ticket's effective product line (its origination plant), not the global
    // map filter — mirrors shouldDisplayQualityFactorsTab.
    // Cement and Aggregates tickets both get diverted.
    const productLine = effectiveProductLine(productLineOverride, selectedProductLine);
    const isDivertibleProductLine = productLine === CEMENT_VALUE || productLine === AGGREGATE_VALUE;
    // The user must be assigned to the ticket's product line. An unresolved product line
    // (undefined) never matches — fail closed, like the backend's ProductLine.None denial.
    // NOTE: secondaryProductLines rehydrates from localStorage as a comma-joined string
    // after a reload. Normalize to an array so membership checks are exact.
    const secondaryProductLinesNormalized = (() => {
      const value = secondaryProductLines as unknown as ProductLine[] | string | undefined;
      if (typeof value === 'string') {
        return value.split(',').filter(Boolean) as ProductLine[];
      }

      return value ?? [];
    })();
    const userIsAssignedToProductLine =
      productLine != null &&
      productLine !== ProductLine.None &&
      (primaryProductLine === productLine || secondaryProductLinesNormalized.includes(productLine));
    // Divert only applies to in-progress tickets. Mirrors backend's own "ticket is open"
    // definition in SendSyncTicketNotification (!Closed && !Voided && !Deleted).
    const isTicketOpen =
      !!selectedTicket &&
      !selectedTicket.closed &&
      !selectedTicket.voided &&
      !selectedTicket.deleted;

    // Divert is an outbound Cement/Aggregates action. The Incoming Materials drill-down
    // resolves productLineOverride from the ticket's origination plant, so an inbound
    // ticket would otherwise pass the product-line gate — close it on that surface.
    return (
      canDivert &&
      isDivertibleProductLine &&
      userIsAssignedToProductLine &&
      isTicketOpen &&
      !this.props.isIncomingMaterials
    );
  }

  private openDeliveryLocationModal = (): void => {
    this.setState({ showDeliveryLocationModal: true });
  };

  // DEV-4153: gate for the inline Mark as Received control rendered in the
  // Statuses section of TicketDetails. Mirrors canEditDeliveryLocation —
  // Ticket owns the gate and the handler; TicketDetails just renders the
  // button when the callback is present. Eligibility = Edit access to the
  // IncomingMaterials flag for the current region plus the shared predicate
  // (unacknowledged, not voided, arrived). Non-incoming-materials tickets
  // carry no currentStatus, so they never qualify.
  private canMarkAsReceived(): boolean {
    const { selectedTicket } = this.props;
    if (!selectedTicket) {
      return false;
    }
    const hasEditAccess = hasFeatureFlagEditAccess(
      this.props.userPermission,
      this.props.currentRegion?.url ?? '',
      INCOMING_MATERIALS_FLAG,
    );
    return canAcknowledgeMaterialReceipt(selectedTicket, hasEditAccess);
  }

  private openMarkAsReceivedConfirm = (): void => {
    const { selectedTicket, openModal, closeModal, openSnackbar, acknowledgeIncomingTicket } =
      this.props;
    if (selectedTicket?.id) {
      openMaterialReceivedConfirm(selectedTicket.id, {
        openModal,
        closeModal,
        openSnackbar,
        acknowledgeIncomingTicket,
      });
    }
  };

  private renderReceiptButton(): JSX.Element | null {
    const { selectedTicket, ticketView } = this.props;
    const hasReceipt = this.hasReceipt();
    const isHidden = ticketView !== VIEW_TICKET_DETAILS || selectedTicket?.closed === false;

    const buttonClassName = cx(
      styles.receiptBtn,
      hasReceipt ? 'tt-btn-secondary--accent' : styles.receiptBtnDisabled,
    );

    const iconClassName = cx(
      'icon-receipt',
      styles.receiptBtnIcon,
      !hasReceipt && styles.receiptBtnIconDisabled,
    );

    return (
      <Tooltip
        text="No receipt available"
        disableHoverListener={hasReceipt}
        disableTouchListener={hasReceipt}
      >
        <span>
          <Button
            dataTest="ticket-view-receipt"
            name="View Receipt"
            buttonClassName={buttonClassName}
            iconClassName={iconClassName}
            hidden={isHidden}
            onClick={this.downloadReceipt}
          />
        </span>
      </Tooltip>
    );
  }

  render(): JSX.Element {
    const { isOpenTicket } = this.props;
    const shouldDisplayQFTab = this.shouldDisplayQualityFactorsTab();
    const defaultView = this.getValidatedView();

    return (
      <Paper id="ticketPopover-paperElement" className={styles.paper} ref={this.printContentRef}>
        <div className={styles.detailHeader}>
          <Tabs value={defaultView} onChange={this.handleTabChange}>
            <Tab label="Details" value={VIEW_TICKET_DETAILS} />
            {!isOpenTicket && <Tab label="Route Info" value={VIEW_TICKET_ROUTE} />}
            {shouldDisplayQFTab && (
              <Tab label="Quality Factors" value={VIEW_TICKET_QUALITY_FACTORS} />
            )}
          </Tabs>

          {isOpenTicket ? this.renderCloseTicketButton() : this.renderPrintButton()}
          {this.state.enableDownloadReceipts && this.renderReceiptButton()}
        </div>
        {this.renderTabContent()}
        {this.state.showDeliveryLocationModal && this.props.selectedTicket && (
          <UpdateDeliveryLocationModal
            selectedTicket={this.props.selectedTicket}
            ticketProductLine={effectiveProductLine(
              this.props.productLineOverride,
              this.props.selectedProductLine,
            )}
            isOpen={this.state.showDeliveryLocationModal}
            onClose={() => this.setState({ showDeliveryLocationModal: false })}
          />
        )}
      </Paper>
    );
  }
}

// Redux Connection
export default connect<any, any, any>(
  (state: any) => ({
    selectedProductLine: state.selectedProductLine,
    selectedTicket: state.selectedTicket,
    ticketView: state.ticketView,
    userPermission: state.userPermission,
    primaryProductLine: state.primaryProductLine,
    secondaryProductLines: state.secondaryProductLines,
    isOpenTicket: state.isOpenTicket,
    ticketsPermissionAccess: state.adminPermissionAccess[OPEN_TICKETS_TEXT] ?? '',
    currentRegion: state.currentRegion,
  }),
  {
    updateTicketView,
    centerTicketPrintView,
    openModal,
    closeModal,
    clearSelectedTicket,
    openSnackbar,
    needToRefreshData,
    acknowledgeIncomingTicket,
  },
)(Ticket as any);
